/**
 * The occurrence sheet, the series editor and the subscribe card an account's Recurring and
 * Cashflow forecast tabs open from a row (TMMAPP-0102 §7). One modal at a time (DLG-005): the sheet
 * closes before the editor opens. Mirrors what `CashflowPanel` wires for the plan table.
 */
import { useState, type ReactNode } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { plannedApi, type Occurrence, type Series } from '@/api/planned';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { OccurrenceSheet } from '@/features/planned/OccurrenceSheet';
import { plannedErrorKey } from '@/features/planned/plannedErrors';
import { SeriesEditorDialog, type EditorMode } from '@/features/planned/SeriesEditorDialog';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';

interface OccurrenceHostOptions {
  /** The account whose tab hosts the dialogs. */
  account: Account;
  /** The amount currency of the sheet when the series' own account has none. */
  currency: string | null;
  onChanged: () => void | Promise<void>;
  onNotify: (message: string) => void;
}

interface OccurrenceHost {
  openOccurrence: (seriesId: number, originalOn: string) => Promise<void>;
  openEditor: (mode: EditorMode, seriesId: number, fromDate?: string) => Promise<void>;
  openCreate: () => void;
  /** Render once, anywhere in the tab. */
  node: ReactNode;
}

export function useOccurrenceHost({
  account,
  currency,
  onChanged,
  onNotify,
}: OccurrenceHostOptions): OccurrenceHost {
  const { t } = useTranslation();
  const [sheet, setSheet] = useState<{
    occ: Occurrence;
    series: Series;
    account: Account;
  } | null>(null);
  const [editor, setEditor] = useState<{
    mode: EditorMode;
    series?: Series;
    fromDate?: string;
  } | null>(null);
  const [subscribe, setSubscribe] = useState(false);

  function fail(err: unknown) {
    if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) setSubscribe(true);
    else onNotify(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
  }

  async function accountOf(series: Series): Promise<Account> {
    return series.accountId === account.id ? account : await accountsApi.get(series.accountId);
  }

  async function openOccurrence(seriesId: number, originalOn: string) {
    try {
      const [series, list] = await Promise.all([
        plannedApi.getSeries(seriesId),
        plannedApi.timeline(seriesId, { from: originalOn, to: originalOn }),
      ]);
      const occ = list.find((row) => row.originalOn === originalOn) ?? list[0];
      if (occ === undefined) return;
      setSheet({ occ, series, account: await accountOf(series) });
    } catch (err) {
      fail(err);
    }
  }

  async function openEditor(mode: EditorMode, seriesId: number, fromDate?: string) {
    try {
      const series = await plannedApi.getSeries(seriesId);
      setEditor({ mode, series, ...(fromDate !== undefined ? { fromDate } : {}) });
    } catch (err) {
      fail(err);
    }
  }

  const node = (
    <>
      {sheet !== null && (
        <OccurrenceSheet
          occurrence={sheet.occ}
          series={sheet.series}
          account={sheet.account}
          owner={isOwnerAccount(sheet.account)}
          currency={sheet.account.currency ?? sheet.account.operatingCurrency ?? currency}
          onClose={() => setSheet(null)}
          onChanged={async () => {
            await onChanged();
            const tl = await plannedApi.timeline(sheet.occ.seriesId, {
              from: sheet.occ.originalOn,
              to: sheet.occ.originalOn,
            });
            const next = tl.find((o) => o.originalOn === sheet.occ.originalOn);
            if (next) setSheet({ ...sheet, occ: next });
          }}
          onEditFollowing={() => {
            setSheet(null);
            void openEditor('following', sheet.series.id, sheet.occ.originalOn);
          }}
          onEditSeries={() => {
            setSheet(null);
            void openEditor('series', sheet.series.id);
          }}
          onOpenSeries={() => setSheet(null)}
          onNotify={onNotify}
        />
      )}
      {editor !== null && (
        <SeriesEditorDialog
          mode={editor.mode}
          accounts={[account]}
          lockedAccountId={account.id}
          series={editor.series}
          fromDate={editor.fromDate}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            onNotify(message);
            setEditor(null);
            void onChanged();
          }}
        />
      )}
      {subscribe && (
        <Dialog
          open
          onClose={() => setSubscribe(false)}
          maxWidth="xs"
          aria-label={t('subscribe.cardTitle')}
        >
          <DialogContent>
            <SubscribeCard />
          </DialogContent>
        </Dialog>
      )}
    </>
  );

  return { openOccurrence, openEditor, openCreate: () => setEditor({ mode: 'create' }), node };
}
